import { ButtonProps } from '@affinis/smartus-components/lib/button/button.types';
import { Chip } from '@affinis/smartus-components/lib/chips/chips.component.types';
import { MenuItem } from '@affinis/smartus-components/lib/menu-extra/menu-extra.component';
import {
  Component,
  Injector,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BaseSearchOptions,
  BaseSelectViewModel,
  ExternalServiceRequestClient,
  IncidentClient,
  IncidentFilterOptions,
  IncidentSearchOptions,
  IncidentTableEntryTaskViewModel,
  IncidentTableEntryViewModel,
  IncidentViews,
  OmContact,
  OptionSetClient,
  ResultWrapperOfIncidentTableEntryViewModel,
  SysEnum,
  TaskClient,
  TaskSearchOptions,
} from '@core/api';
import { RoutePath } from '@core/routing/routing.types';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { IbpdiHelperService } from '@core/services/translate-service/ibpdi-helper.service';
import { OplExtService } from '@core/services/opl-ext-service/opl-ext.service';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ModalFilterComponent } from '@shared/components/modal-filter/modal-filter.component';
import { TableBaseComponent } from '@shared/components/table-base/table-base';
import { ITableConfig } from '@shared/components/table-base/table-base.types';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { excelExport } from '@shared/utils/excel-export';
import moment from 'moment';
import { Subscription, finalize, first, switchMap, tap } from 'rxjs';

interface TicketTaskOption {
  label: string;
  value: string;
}

const filterOptionsKey = 'filterOptionsTickets';
const ticketTaskSelectionKey = 'ticketTaskSelection';

@Component({
  selector: 'app-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss'],
  standalone: false,
})
export class TicketListTelekomComponent
  extends TableBaseComponent
  implements OnInit
{
  override config = this.configure(<ITableConfig>{
    title: 'taskmodule.tickets.list.title',
    translatePath: 'taskmodule.shared',
  });

  override tableIdentifier = 'ticketList';

  form = new FormGroup({});
  user: OmContact;

  @ViewChild('ticketActionsTemplate', { static: true })
  ticketActionsTemplateRef!: TemplateRef<void>;

  controlButtons: ButtonProps[] = [
    {
      label: this.translateService.instant('crud.refresh'),
      onClick: () => this.refresh(),
    },
  ];

  private optionSetSubscription: Subscription;

  organisationName: string;

  organisations: string[] = [
    'ISS Vermietung',
    'ISS Property Management',
    'ISS An-/Abmietung',
    'ISS Capital Projects',
    'ISS Vertragsmanagement',
  ];

  canDownload: boolean = false;

  loading = false;

  dialogData;

  isOPLExt = false;

  ticketTaskControl = new FormControl('TicketsWithActiveTasks');

  constructor(
    injector: Injector,
    private readonly ibpdiHelperService: IbpdiHelperService,
    private readonly incidentClient: IncidentClient,
    private readonly taskClient: TaskClient,
    private readonly optionSetClient: OptionSetClient,
    private readonly breadcrumbsService: BreadcrumbService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly externalServiceRequestClient: ExternalServiceRequestClient,
    private readonly oplExtService: OplExtService
  ) {
    super(injector);
  }

  public ngOnInit() {
    this.breadcrumbsService.setBreadcrumbs([
      {
        label: this.translateService.instant(
          'menu.tickets_tasks.component.tickets_view'
        ),
      },
    ]);

    this.columns = [
      {
        field: 'showExpand',
        showExpand: true,
        sortable: false,
        resizable: false,
        width: '90px',
      },
      {
        header: this.translate('label__ticketNumber'),
        field: 'ticketNumber',
        sortable: true,
        class: '',
        resizable: true,
        minWidth: 180,
      },
      {
        header: this.translate('label__incidentTitle'),
        field: 'title',
        sortable: true,
        class: 'truncated',
        resizable: true,
        width: '200dp',
        minWidth: 200,
      },
      {
        header: this.translate('label__incidentDescription'),
        field: 'description',
        sortable: true,
        class: 'truncated',
        resizable: true,
        minWidth: 195,
        hide: true,
      },
      {
        header: this.translate('label__incidentClass'),
        field: 'ticketClass',
        sortable: true,
        class: '',
        resizable: true,
      },
      {
        header: this.translate('label__economicUnit'),
        field: 'economicUnit',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__postalCode'),
        field: 'postalCode',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__incidentCity'),
        field: 'city',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__incidentStreet'),
        field: 'street',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__createdOn'),
        field: 'createdOn',
        sortable: true,
        class: 'truncated',
        formatter: (data: any) =>
          data.createdOn
            ? moment(data.createdOn)
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_time_format'
                  )
                ) +
              ' ' +
              this.translate('extended_time_str')
            : '--',
        resizable: true,
        width: '140px',
        minWidth: 134,
      },
      {
        header: this.translate('label__statusReason'),
        field: 'status',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__documents'),
        width: '120px',
        field: 'action',
        cellTemplate: this.ticketActionsTemplateRef,
        resizable: true,
      },
    ];

    this.loadColumnVisibilityAndOrder();

    this.userClient.getUserProfile().subscribe({
      next: profile => {
        this.user = profile;
      },
      error: err => {
        if (err.errorCode === 5002) {
          let count = 0;
          this.userService.isGsusUserBehavior$.subscribe({
            next: isGsus => {
              this.sysUser = isGsus;
              if (this.sysUser) {
                this.ticketTaskOptions = [
                  {
                    label: this.translateService.instant(
                      'taskmodule.tickets.list.ticket_task_selection.active_tickets'
                    ),
                    value: 'TicketsWithActiveTasks',
                  },
                  {
                    label: this.translateService.instant(
                      'taskmodule.tickets.list.ticket_task_selection.all_tickets'
                    ),
                    value: 'All',
                  },
                ];
                this.ensureValidTicketTaskSelection();
                if (
                  !this.filterFields.some(
                    field => field.key === 'organisation'
                  ) &&
                  count === 0
                ) {
                  count = 1;
                  this.filterFields[0].fieldGroup.push({
                    type: 'select',
                    key: 'organisation',
                    props: {
                      label: 'Firma',
                      options: this.organisations.map(organisation => ({
                        label: organisation,
                        value: organisation,
                      })),
                    },
                  });
                }
              }
            },
          });
        }
      },
    });

    const storedTicketTaskSelection = this.sessionStorageService.get(
      ticketTaskSelectionKey
    )?.ticketTaskSelection;

    if (storedTicketTaskSelection) {
      this.ticketTaskControl.setValue(storedTicketTaskSelection);
      this.ensureValidTicketTaskSelection();
    }

    const filterOptions = this.sessionStorageService.get(filterOptionsKey);

    this.config.grid.filter.filterOptions = filterOptions;

    this.mapFormModelToChips(filterOptions);
    this.updateFilter({
      ...filterOptions,
      ticketClassList: filterOptions.ticketClassList
        ? filterOptions.ticketClassList.map(x => x.id)
        : null,
      ticketTaskSelection: this.ticketTaskControl.value,
    });
    this.refreshFromFilter();

    this.dialogData = {
      columns: this.columns,
      filterFields: this.filterFields,
      filterValues: this.config.grid.filter.filterOptions,
      actions: {
        onSubmit: (formModel: any) => {
          this.sessionStorageService.set(filterOptionsKey, formModel);
          this.mapFormModelToChips(formModel);
          this.updateFilter({
            ...formModel,
            ticketClassList: formModel.ticketClassList
              ? formModel.ticketClassList.map(x => x.id)
              : null,
            ticketTaskSelection: this.ticketTaskControl.value,
          });

          this.refreshFromFilter();
          this.dialogData.filterValues = this.config.grid.filter.filterOptions;
          this.organisationName =
            this.config.grid.filter.filterOptions.organisation;
        },
        onReset: () => {
          this.sessionStorageService.set(filterOptionsKey, {});
          this.mapFormModelToChips({});
          this.updateFilter({
            ticketTaskSelection: this.ticketTaskControl.value,
          });
          this.refreshFromFilter();
        },
      },
    };
  }

  chips: Chip[] = [];

  menuItems: MenuItem[] = [];

  override columns: MtxGridColumn[] = [];

  ticketTaskOptions: TicketTaskOption[] = [
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.active_tickets'
      ),
      value: 'TicketsWithActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.all_tickets'
      ),
      value: 'All',
    },
  ];

  override filterFields: Array<FormlyFieldConfig> = [
    {
      type: 'flex-layout',
      fieldGroup: [
        {
          key: 'ticketNumber',
          type: 'input',
          props: {
            label: this.translate('label__ticketNumber'),
          },
        },
        {
          key: 'name',
          type: 'input',
          props: {
            label: this.translate('label__title'),
          },
        },
        {
          key: 'ticketDescription',
          type: 'input',
          props: {
            label: this.translate('label__incidentDescription'),
          },
        },
        {
          key: 'ticketClassList',
          type: 'select',
          props: {
            label: this.translate('label__incidentClass'),
            options: [],
            multiple: true,
            compareWith: (o1: any, o2: any) => o1?.id === o2?.id,
          },
          hooks: {
            onInit: field => {
              if (field?.props != null) {
                const f = field.props;

                if (
                  f.options &&
                  (f.options as Array<BaseSelectViewModel>).length > 0
                ) {
                  this.setTicketClassDefaultValue(
                    field.formControl as FormControl
                  );

                  return;
                }

                f.loading = true;
                this.loading = true;

                const apiCall = this.isOPLExt
                  ? this.incidentClient.getIncidentClassesByRole(
                      UserRoles.SMARTPORTAL_OPL_EXT
                    )
                  : this.incidentClient.getIncidentClassesForDL();

                this.optionSetSubscription = apiCall
                  .pipe(
                    first(),
                    finalize(() => {
                      f.readonly = false;
                      if (field?.props != null) {
                        f.loading = false;
                      }
                      this.loading = false;
                      this.cd.detectChanges();
                    })
                  )
                  .subscribe({
                    next: (res: BaseSelectViewModel[]) => {
                      f.options = res.map(option => {
                        return {
                          label: option.name,
                          value: option,
                        };
                      });

                      this.setTicketClassDefaultValue(
                        field.formControl as FormControl
                      );
                    },
                    error: () => {
                      this.toastr.error(
                        this.translateService.instant(
                          'taskmodule.tickets.list.dtag.error.ticketClass_loading'
                        )
                      );
                    },
                  });
              }
            },
            onDestroy: _field => {
              if (!this.optionSetSubscription.closed) {
                this.optionSetSubscription.unsubscribe();
              }
            },
          },
        },
        {
          key: 'economicUnit',
          type: 'input',
          props: {
            label: this.translate('label__economicUnit'),
          },
        },
        {
          key: 'postalcode',
          type: 'input',
          props: {
            label: this.translate('label__postalCode'),
          },
        },
        {
          key: 'city',
          type: 'input',
          props: {
            label: this.translate('label__incidentCity'),
          },
        },
        {
          key: 'streetName',
          type: 'input',
          props: {
            label: this.translate('label__incidentStreet'),
          },
        },
        {
          type: 'flex-layout-col',
          fieldGroup: [
            {
              type: 'datepicker',
              key: 'createDateFrom',
              props: {
                label: this.translate('label__createDateFrom'),
              },
            },
            {
              type: 'datepicker',
              key: 'createDateTo',
              props: {
                label: this.translate('label__createDateTo'),
              },
            },
          ],
        },
        {
          key: 'statusreasonList',
          type: 'select',
          props: {
            label: this.translate('label__statusReason'),
            options: [],
            multiple: true,
            loading: true,
            readonly: true,
          },
          hooks: {
            onInit: field => {
              if (field?.props != null) {
                const f = field.props;

                if (f.options && (f.options as Array<any>).length > 0) {
                  return;
                }
                f.loading = true;
                this.optionSetSubscription = this.optionSetClient
                  .getOptionSetByString('Incident_StatusCode')
                  .pipe(first())
                  .subscribe({
                    next: (res: SysEnum[]) => {
                      f.options = res
                        .filter(
                          option =>
                            option.defaultName !==
                              'Bereitgestellte Informationen' &&
                            option.defaultName !== 'technische Dublette'
                        )
                        .map(option => {
                          return {
                            label:
                              this.ibpdiHelperService.getTranslatedEnumLable(
                                option
                              ),
                            value: option.defaultName,
                          };
                        });
                      f.readonly = false;
                      if (field?.props != null) {
                        f.loading = false;
                      }
                      this.cd.detectChanges();
                    },
                  });
              }
            },
            onDestroy: _field => {
              if (!this.optionSetSubscription.closed) {
                this.optionSetSubscription.unsubscribe();
              }
            },
          },
        },
      ],
    },
  ];

  modalComponent = ModalFilterComponent;

  override getTableData$ = (
    query?: BaseSearchOptions,
    filterOptions?: IncidentFilterOptions
  ) => {
    (this.form.value as any).ticketTaskSelection =
      filterOptions?.ticketTaskSelection == null
        ? 'TicketsWithActiveTasks'
        : filterOptions?.ticketTaskSelection;
    let view: IncidentViews = IncidentViews.VisibleByTaskAssociation;

    if ((this.form.value as any).ticketTaskSelection === 'All') {
      view = IncidentViews.VisibleByOneTaskAssociation;
    } else if (
      (this.form.value as any).ticketTaskSelection ===
      'MyTicketsWithActiveTasks'
    ) {
      view = IncidentViews.VisibleByMyTaskAssociation;
    }
    const searchOptions: IncidentSearchOptions = {
      ...query,
      filterOptions,
      viewOptions: { selectedView: view },
    };

    return this.oplExtService.checkIsOplExt().pipe(
      switchMap(hasRole => {
        if (hasRole) {
          this.isOPLExt = true;
          return this.externalServiceRequestClient
            .getExternalServiceRequests(searchOptions)
            .pipe(
              tap(() => {
                this.canDownload = true;
              })
            );
        } else {
          return this.incidentClient
            .getIncidentsAuthenticated(searchOptions)
            .pipe(
              tap(() => {
                this.canDownload = true;
              })
            );
        }
      })
    );
  };

  getTasks(incident: IncidentTableEntryViewModel) {
    if (
      this.config.grid.filter.filterOptions.ticketTaskSelection ===
      'TicketsWithActiveTasks'
    ) {
      if (this.config.grid.filter.filterOptions.organisation) {
        incident.tasks = incident.tasks?.filter(
          x => x.organisationName === this.organisationName
        );
      } else if (this.sysUser) {
        incident.tasks = incident.tasks?.filter(x => this.checkOrganisation(x, incident.ticketClass));
      }
    }
    const searchOptions: TaskSearchOptions = {
      filterOptions: { ticketNumber: incident.ticketNumber },
    };
    this.taskClient.getTasks(searchOptions);
  }

  override clickRow = (data, router: Router, route: ActivatedRoute) => {
    router.navigate(['..', 'tasks'], {
      relativeTo: route,
      queryParams: {
        ticketNumber: data.rowData.ticketNumber,
      },
    });
  };

  clickExpandedRow(row: IncidentTableEntryTaskViewModel) {
    if (!row.isActive && row.organisationId === this.user.organisationId) {
      this.router.navigate(['/task/viewDetails', row.taskId], {
        state: { originRoute: 'tickets' },
      });
      return;
    }

    if (!row.isActive && row.organisationId !== this.user.organisationId)
      return;

    if (
      row.status === 'Abgeschlossen' ||
      (row.status === 'Storniert' &&
        row.organisationId === this.user.organisationId)
    ) {
      this.router.navigate([RoutePath.TASK_VIEW.replace(':id', row.taskId)], {
        state: { originRoute: 'tickets' },
      });
    } else {
      this.router.navigate([RoutePath.TASK.replace(':id', row.taskId)], {
        state: { originRoute: 'tickets' },
      });
    }
  }

  clickExpandedRowGSUS(row: IncidentTableEntryTaskViewModel) {
    this.router.navigate(['/task/viewDetails', row.taskId], {
      state: { originRoute: 'tickets' },
    });
  }

  openDocuments(event: MouseEvent, ticket: IncidentTableEntryViewModel) {
    event.stopPropagation();
    this.router.navigate([
      `${RoutePath.DOCUMENTS}/ticket/${ticket.ticketNumber}`,
    ]);
    return false;
  }

  openContact(event: MouseEvent, row: IncidentTableEntryTaskViewModel): void {
    event.stopPropagation();
    const taskId = row.taskId;

    const url = `/task/details/${taskId}`;
    this.router
      .navigate([url], {
        queryParams: { tab: 'contact' },
        state: { originRoute: 'tickets' },
      })
      .catch(err => {
        console.error('Navigation error:', err);
      });
  }

  openContactDetails(
    event: MouseEvent,
    row: IncidentTableEntryTaskViewModel
  ): void {
    event.stopPropagation();
    const taskId = row.taskId;

    const url = `/task/viewDetails/${taskId}`;
    this.router
      .navigate([url], {
        queryParams: { tab: 'contact' },
        state: { originRoute: 'tickets' },
      })
      .catch(err => {
        console.error('Navigation error:', err);
      });
  }

  checkOrganisation(task: IncidentTableEntryTaskViewModel, ticketClass?: string) {
    if (this.config.grid.filter.filterOptions.organisation) {
      return (
        this.config.grid.filter.filterOptions.organisation ===
        task.organisationName
      );
    }
    if (this.isOPLExt) {
      return (
        task.organisationName ===
        'Dienstleister für optionale Leistungen beauftragen extern'
      );
    }
    // Für OPL-Ext-Tickets (ISS-SysUser-Sicht): nur externe Dienstleister-Aufgaben anzeigen
    if (ticketClass === 'Optionale Leistungen Extern') {
      return !!task.organisationName && !this.organisations.includes(task.organisationName);
    }
    // Für andere Ticketklassen: ISS-Aufgaben anzeigen (mit oder ohne Org-Name)
    if (!task.organisationName) {
      return true;
    }
    return this.organisations.includes(task.organisationName);
  }

  onCloseDialog = (e: any) => {
    console.log('[Ticket list] onCloseDialog', e);
  };

  handleOnRemoveChip = (eventObj: any) => {
    delete this.config.grid.filter.filterOptions[eventObj.id];

    this.sessionStorageService.set(filterOptionsKey, {
      ...this.config.grid.filter.filterOptions,
      ticketClassList: this.config.grid.filter.filterOptions.ticketClassList
        ? this.sessionStorageService.get(filterOptionsKey).ticketClassList
        : null,
    });
    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);
    this.refreshFromFilter();
  };

  mapFormModelToChips = formModel => {
    this.chips = [];
    this.chips.length = 0;

    Object.keys(formModel).forEach(formModelItemKey => {
      const flattened = this.filterFields[0].fieldGroup?.flatMap(field =>
        field.fieldGroup ? [field, ...field.fieldGroup] : field
      );
      const field = flattened?.find(fieldItem => {
        return fieldItem.key === formModelItemKey;
      });
      if (field) {
        if (
          formModelItemKey === 'createDateTo' ||
          formModelItemKey === 'createDateFrom'
        ) {
          const date = new Date(formModel[formModelItemKey]).toLocaleDateString(
            'de-DE'
          );
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${date}`,
          });
        } else if (
          formModelItemKey === 'ticketClassList' &&
          formModel[formModelItemKey]
        ) {
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${formModel[formModelItemKey].map(x => x.name)}`,
          });
        } else if (formModel[formModelItemKey]) {
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${formModel[formModelItemKey]}`,
          });
        }
      }
    });
  };

  onSelectionChange() {
    this.sessionStorageService.set(ticketTaskSelectionKey, {
      ticketTaskSelection: this.ticketTaskControl.value,
    });
    this.updateFilter({
      ...this.config.grid.filter.filterOptions,
      ticketTaskSelection: this.ticketTaskControl.value,
    });
    this.refreshFromFilter();
  }

  private ensureValidTicketTaskSelection() {
    const currentValue = this.ticketTaskControl.value;
    const isValidValue = this.ticketTaskOptions.some(
      option => option.value === currentValue
    );

    if (isValidValue) {
      return;
    }

    const fallbackValue =
      this.ticketTaskOptions[0]?.value || 'TicketsWithActiveTasks';
    this.ticketTaskControl.setValue(fallbackValue);
    this.sessionStorageService.set(ticketTaskSelectionKey, {
      ticketTaskSelection: fallbackValue,
    });
  }

  exportToExcel() {
    this.canDownload = false;
    this.getTableData$(null, this.config.grid.filter.filterOptions).subscribe({
      next: (res: ResultWrapperOfIncidentTableEntryViewModel) => {
        const data = res.entites;

        const columnMapping = {
          ticketNumber: 'Ticketnummer',
          title: 'Titel',
          ticketClass: 'Ticketklasse',
          economicUnit: 'Wirtschaftseinheit',
          postalCode: 'Postleitzahl',
          city: 'Stadt',
          street: 'Straße',
          createdOn: 'Erstellt am',
          status: 'Status',
          description: 'Beschreibung',
        };

        excelExport(data, columnMapping, 'Tickets', 'DL-Tickets.xlsx');
      },
    });
  }

  setTicketClassDefaultValue(fc: FormControl) {
    fc.setValue(null);

    const savedFilters = this.sessionStorageService.get('filterOptionsTickets');

    if (savedFilters?.ticketClassList && fc) {
      const defaultValue = savedFilters.ticketClassList.map(option => ({
        id: option.id,
        name: option.name,
      }));

      fc.setValue(defaultValue);
    }
  }
}
